import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { clearCurrentKnockStatus } from '@user/store/knockStatus/api'
import {
  selectKnockStatusSavedAt,
  selectKnockStatusSavedLocationId,
} from '@user/store/knockStatus/slice'
import { toast } from '@sales-center/components'
import {
  classifyKnockStatusVariant,
  KnockStatusVariantKind,
} from './knockStatusVariant'

type Props = {
  knockStatusLocationId: number | null
  /** Lowercased map variant, e.g. 'lead' | 'opportunity' | 'draftlead' | 'pendinglead'. */
  variant: string
  /**
   * Explicit entityType override. When set, it is sent as-is and the variant mapping is skipped.
   * Standalone pins (knock-status-only + PestPac) pass 'Unknown', which the backend resolves to
   * 'KnockStatus' (SH-851).
   */
  entityType?: string
  onRefresh?: () => void
}

// Maps the map variant to the entityType the clear endpoint expects (taxonomy in knockStatusVariant).
const ENTITY_TYPE_BY_KIND: Record<KnockStatusVariantKind, string> = {
  opportunity: 'Opportunity',
  draftLead: 'DraftLead',
  lead: 'Lead',
}
const mapVariantToEntityType = (variant: string): string =>
  ENTITY_TYPE_BY_KIND[classifyKnockStatusVariant(variant)]

// Clears the active knock status (SH-661) from a map preview (bubble or drawer).
// History, notes, and timeline are kept; onRefresh re-fetches to drop the badge.
export function useClearKnockStatus({
  knockStatusLocationId,
  variant,
  entityType,
  onRefresh,
}: Props) {
  const dispatch = useAppDispatch()
  const knockStatusSavedAt = useAppSelector(selectKnockStatusSavedAt)
  const knockStatusSavedLocationId = useAppSelector(
    selectKnockStatusSavedLocationId,
  )
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  // SH-1781: bubble data doesn't refresh after a clear, so give callers a
  // direct signal instead. Known limit: recreating a status for this same
  // location in the same open bubble still shows as cleared until reopened.
  const [cleared, setCleared] = useState(false)

  // Some callers render with no key, so React reuses this hook's instance
  // across pins -- reset on location change so a clear can't leak between them.
  useEffect(() => {
    setCleared(false)
  }, [knockStatusLocationId])

  // A new save for this same location doesn't change knockStatusLocationId,
  // so it needs its own reset trigger. knockStatusSavedAt/LocationId are set
  // by every knock-status save thunk (create, add-history, edit-reason) in
  // store/knockStatus/slice.ts, so this covers every save path -- not just
  // ones routed through a specific modal -- while still only resetting the
  // bubble whose own location was actually saved.
  useEffect(() => {
    if (
      knockStatusSavedAt &&
      knockStatusSavedLocationId === knockStatusLocationId
    ) {
      setCleared(false)
    }
  }, [knockStatusSavedAt, knockStatusSavedLocationId, knockStatusLocationId])

  const openClearConfirm = useCallback(() => setClearConfirmOpen(true), [])
  const closeClearConfirm = useCallback(() => setClearConfirmOpen(false), [])

  const handleClearKnockStatus = useCallback(async () => {
    if (!knockStatusLocationId) return
    setIsClearing(true)
    try {
      await dispatch(
        clearCurrentKnockStatus({
          locationId: knockStatusLocationId,
          entityType: entityType ?? mapVariantToEntityType(variant),
        }),
      ).unwrap()
      toast.success('Knock status cleared')
      setClearConfirmOpen(false)
      setCleared(true)
      onRefresh?.()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to clear knock status')
    } finally {
      setIsClearing(false)
    }
  }, [dispatch, knockStatusLocationId, variant, entityType, onRefresh])

  return {
    clearConfirmOpen,
    isClearing,
    cleared,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
  }
}
